import { ReactNode, useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import type { Tables, TablesInsert } from '@/integrations/supabase/types';
import { useUserRole } from '@/hooks/useUserRole';
import { useContentHealth, getCardDueInfo } from '@/hooks/useContentHealth';
import { useApp } from '@/contexts/AppContext';
import { ArrowLeft, type LucideIcon, Crown, Shield, Users, FileText, Copy, ClipboardCheck, CheckCheck, EyeOff, RotateCcw, UserRound, Activity, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { format, differenceInDays, addMonths, formatDistanceToNow } from 'date-fns';
import {
  buildProductAnalyticsSummary,
  EMPTY_PRODUCT_ANALYTICS_SUMMARY,
  type ProductAnalyticsSummary,
} from '../../shared/observability';
import {
  AppButton,
  AppCard,
  AppCardBody,
  AppCardHeader,
  AppDisclosureGlyph,
  AppHeaderBar,
  AppIconButton,
  AppLoadingState,
  AppPanelButton,
  AppPage,
  AppSectionHeader,
  AppTextArea,
} from '../../ui-foundation';

type CommunityPost = Tables<'community_posts'>;
type CommunityReply = Tables<'community_replies'>;
type CommunityReport = Tables<'community_reports'>;
type CommunityModerationLog = Tables<'community_moderation_log'>;
type CommunityModerationAppeal = Tables<'community_moderation_appeals'>;
type CommunityReportStatus = CommunityReport['status'];
type ProfileRow = Tables<'profiles'>;
type ModerationItem = {
  report: CommunityReport;
  post: CommunityPost | null;
  reply: CommunityReply | null;
};
type RestrictedMember = {
  userId: string;
  profile: ProfileRow | null;
  unresolvedReports: number;
  latestAction: CommunityModerationLog | null;
};
type AppealQueueItem = {
  appeal: CommunityModerationAppeal;
  moderationLog: CommunityModerationLog | null;
  profile: ProfileRow | null;
};
type RuntimeEventItem = {
  event: {
    id: string;
    user_id: string;
    severity: 'error' | 'warning';
    source: string;
    path: string | null;
    message: string;
    status: 'open' | 'resolved';
    resolution_note: string | null;
    created_at: string;
  };
  profile: ProfileRow | null;
};
type ProductEventRow = {
  id: string;
  user_id: string;
  event_name: string;
  path: string | null;
  created_at: string;
  metadata: Record<string, unknown> | null;
};
type AdminMemberDirectoryEmailRow = Pick<Tables<'profiles'>, 'user_id'> & {
  email: string | null;
};

const COMMUNITY_GUIDELINES = `Adulting community guidelines: Be kind. No selling or self-promotion. Share your experience, not advice you're not qualified to give. No judgment — everyone's situation is different. This is a safe space. Treat it like one.`;

const HANDBOOK_SCENARIOS = [
  {
    title: 'Someone in crisis',
    body: "Always signpost to professional help first. Do not try to counsel. Say: 'I can see you're going through something really hard. Please reach out to Samaritans on 116 123 — they're available 24/7 and it's free.' Then escalate to admin.",
  },
  {
    title: 'Spam or promotional content',
    body: "Remove it immediately. If it looks coordinated, alert the admin. Don't engage with the poster publicly.",
  },
  {
    title: 'Conflict between members',
    body: "Don't take sides. Post the community guidelines as a reply. If it escalates, lock the thread and escalate to admin.",
  },
  {
    title: 'Abusive or harmful content',
    body: 'Remove immediately. First offence: send a private warning. Second offence: escalate to admin for suspension.',
  },
];

const ESCALATION_STEPS = [
  {
    title: 'Crisis or immediate risk',
    body: 'Signpost to Samaritans or NHS 111 first. Do not try to counsel in-thread. Hide harmful content if needed and escalate to the admin the same day.',
  },
  {
    title: 'Abuse, hate, threats, or targeted harm',
    body: 'Hide the content, resolve the report, and flag it to the admin with context. Repeat behaviour should not be handled as an ordinary disagreement.',
  },
  {
    title: 'Spam, selling, or coordinated promotion',
    body: 'Hide and resolve quickly. If the pattern looks repeated or automated, tell the admin so account-level action can be considered.',
  },
  {
    title: 'Conflict or misinformation',
    body: 'Do not debate endlessly in-thread. Point back to lived experience and the ground rules. Escalate if it starts to make the space feel unsafe.',
  },
];

function reportStatusMeta(status: CommunityReportStatus) {
  if (status === 'in_review') return { label: 'In review', className: 'bg-amber-100 text-amber-800' };
  if (status === 'actioned') return { label: 'Actioned', className: 'bg-primary/10 text-primary' };
  if (status === 'dismissed') return { label: 'Dismissed', className: 'bg-muted text-muted-foreground' };
  return { label: 'Open', className: 'bg-rose-100 text-rose-800' };
}

function reportStatusOrder(status: CommunityReportStatus) {
  if (status === 'open') return 0;
  if (status === 'in_review') return 1;
  if (status === 'actioned') return 2;
  return 3;
}

function formatAdminMemberName(firstName?: string | null, email?: string | null) {
  const trimmedFirstName = firstName?.trim();
  if (trimmedFirstName) return trimmedFirstName;

  const emailLocalPart = email?.split('@')[0]?.trim();
  if (emailLocalPart) return emailLocalPart;

  return 'Adulting member';
}

function AdminWorkspaceSection({
  eyebrow,
  title,
  subtitle,
  accented,
  action,
  collapsible,
  open = true,
  onToggle,
  children,
}: {
  eyebrow: string;
  title: string;
  subtitle?: string;
  accented?: boolean;
  action?: ReactNode;
  collapsible?: boolean;
  open?: boolean;
  onToggle?: () => void;
  children: ReactNode;
}) {
  const sectionAction = collapsible ? (
    <div className="flex items-center gap-2">
      {action}
      <AppIconButton
        onClick={onToggle}
        title={open ? `Collapse ${title}` : `Open ${title}`}
        className="shrink-0"
      >
        <AppDisclosureGlyph open={open} />
      </AppIconButton>
    </div>
  ) : action;

  return (
    <AppCard className={cn('overflow-hidden', accented ? 'border-primary/20 bg-primary/5' : undefined)}>
      <AppCardHeader className="border-b border-border bg-primary/10">
        <div className="space-y-2">
          <div className="inline-flex w-fit rounded-full border border-primary/15 bg-background/90 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.08em] text-primary">
            {eyebrow}
          </div>
          <AppSectionHeader title={title} subtitle={subtitle} action={sectionAction} />
        </div>
      </AppCardHeader>
      {open ? <AppCardBody className="space-y-4">{children}</AppCardBody> : null}
    </AppCard>
  );
}

function AdminSummaryTile({
  label,
  value,
  detail,
  onPress,
}: {
  label: string;
  value: string | number;
  detail: string;
  onPress?: () => void;
}) {
  const content = (
    <>
      <p className="text-[10px] font-semibold uppercase tracking-[0.08em] text-primary">{label}</p>
      <p className="mt-1 text-2xl font-bold text-foreground">{value}</p>
      <p className="mt-1 text-[11px] leading-relaxed text-muted-foreground">{detail}</p>
    </>
  );

  if (onPress) {
    return (
      <AppPanelButton
        onClick={onPress}
        className="rounded-xl border border-primary/15 bg-primary/5 px-4 py-3 text-left transition hover:border-primary/30 hover:bg-primary/10"
      >
        <div className="w-full">{content}</div>
      </AppPanelButton>
    );
  }

  return (
    <div className="rounded-xl border border-primary/15 bg-primary/5 px-4 py-3">
      {content}
    </div>
  );
}

function AdminLauncherCard({
  eyebrow,
  title,
  summary,
  detail,
  icon: Icon,
  onPress,
}: {
  eyebrow: string;
  title: string;
  summary: string;
  detail?: string;
  icon: LucideIcon;
  onPress: () => void;
}) {
  return (
    <AppPanelButton
      onClick={onPress}
      className="rounded-xl border border-border bg-card p-4 text-left transition-colors hover:border-primary/30 hover:bg-primary/5"
    >
      <div className="flex items-start gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
          <Icon size={18} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="mb-1 inline-flex rounded-full border border-primary/15 bg-primary/5 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.08em] text-primary">
            {eyebrow}
          </div>
          <p className="text-sm font-semibold text-foreground">{title}</p>
          <p className="mt-1 text-xs leading-relaxed text-muted-foreground">{summary}</p>
          {detail ? <p className="mt-2 text-[11px] font-medium text-primary">{detail}</p> : null}
        </div>
      </div>
    </AppPanelButton>
  );
}

export default function AdminPage() {
  const navigate = useNavigate();
  const { user } = useApp();
  const { role, isAdmin, isAdminOrMod, loading } = useUserRole();
  const [stats, setStats] = useState({ users: 0, benefits: 0, content: 0 });
  const [statsLoading, setStatsLoading] = useState(true);
  const [communityQueue, setCommunityQueue] = useState({ reports: 0, livePosts: 0 });
  const [communityQueueLoading, setCommunityQueueLoading] = useState(true);
  const [moderationItems, setModerationItems] = useState<ModerationItem[]>([]);
  const [moderationLoading, setModerationLoading] = useState(true);
  const [moderatingId, setModeratingId] = useState<string | null>(null);
  const [reportNoteDrafts, setReportNoteDrafts] = useState<Record<string, string>>({});
  const [restrictedMembers, setRestrictedMembers] = useState<RestrictedMember[]>([]);
  const [restrictedMembersLoading, setRestrictedMembersLoading] = useState(true);
  const [appealItems, setAppealItems] = useState<AppealQueueItem[]>([]);
  const [appealsLoading, setAppealsLoading] = useState(true);
  const [appealResolutionDrafts, setAppealResolutionDrafts] = useState<Record<string, string>>({});
  const [reviewingAppealId, setReviewingAppealId] = useState<string | null>(null);
  const [runtimeItems, setRuntimeItems] = useState<RuntimeEventItem[]>([]);
  const [runtimeLoading, setRuntimeLoading] = useState(true);
  const [runtimeResolutionDrafts, setRuntimeResolutionDrafts] = useState<Record<string, string>>({});
  const [resolvingRuntimeId, setResolvingRuntimeId] = useState<string | null>(null);
  const [analyticsSummary, setAnalyticsSummary] = useState<ProductAnalyticsSummary>(EMPTY_PRODUCT_ANALYTICS_SUMMARY);
  const [memberEmailByUserId, setMemberEmailByUserId] = useState<Record<string, string | null>>({});
  const [analyticsLoading, setAnalyticsLoading] = useState(true);
  const { sections, totalDue, loading: contentLoading } = useContentHealth();
  const [handbookOpen, setHandbookOpen] = useState(false);
  const [scenarioOpen, setScenarioOpen] = useState<string | null>(null);
  const [guidelinesCopied, setGuidelinesCopied] = useState(false);
  const [quarterlyPromptCopied, setQuarterlyPromptCopied] = useState(false);
  const [openAdminSections, setOpenAdminSections] = useState({
    moderation: true,
    tools: false,
    ops: false,
    reference: false,
  });

  const toggleAdminSection = (section: keyof typeof openAdminSections) => {
    setOpenAdminSections((current) => ({ ...current, [section]: !current[section] }));
  };

  useEffect(() => {
    if (!loading && !isAdminOrMod) navigate('/money', { replace: true });
  }, [loading, isAdminOrMod, navigate]);

  useEffect(() => {
    if (!isAdmin) return;
    async function fetchStats() {
      const [pr, bv, cc] = await Promise.all([
        supabase.from('profiles').select('id', { count: 'exact', head: true }),
        supabase.from('benefits_viewed').select('id', { count: 'exact', head: true }),
        supabase.from('content_cards').select('id', { count: 'exact', head: true }),
      ]);
      setStats({
        users: pr.count || 0,
        benefits: bv.count || 0,
        content: cc.count || 0,
      });
      setStatsLoading(false);
    }
    fetchStats();
  }, [isAdmin]);

  useEffect(() => {
    if (!isAdmin) {
      setMemberEmailByUserId({});
      return;
    }

    async function fetchMemberDirectoryEmails() {
      const { data, error } = await supabase.rpc('get_admin_members_directory');

      if (error) {
        console.error(error);
        setMemberEmailByUserId({});
        return;
      }

      const nextEmailMap: Record<string, string | null> = {};
      ((data || []) as AdminMemberDirectoryEmailRow[]).forEach((member) => {
        nextEmailMap[member.user_id] = member.email;
      });
      setMemberEmailByUserId(nextEmailMap);
    }

    void fetchMemberDirectoryEmails();
  }, [isAdmin]);

  useEffect(() => {
    if (!isAdmin) return;

    async function fetchAnalyticsSummary() {
      setAnalyticsLoading(true);
      const since = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString();

      const { data, error } = await supabase
        .from('product_events' as any)
        .select('id, user_id, event_name, path, created_at, metadata')
        .gte('created_at', since)
        .order('created_at', { ascending: false })
        .limit(1000);

      if (error) {
        console.error(error);
        setAnalyticsSummary(EMPTY_PRODUCT_ANALYTICS_SUMMARY);
        setAnalyticsLoading(false);
        return;
      }

      const events = (data || []) as ProductEventRow[];
      setAnalyticsSummary(buildProductAnalyticsSummary(events));
      setAnalyticsLoading(false);
    }

    void fetchAnalyticsSummary();
  }, [isAdmin]);

  const fetchCommunityQueue = useCallback(async () => {
    if (!isAdminOrMod) return;

    setCommunityQueueLoading(true);

    const [reports, livePosts] = await Promise.all([
      supabase
        .from('community_reports')
        .select('id', { count: 'exact', head: true })
        .in('status', ['open', 'in_review']),
      supabase
        .from('community_posts')
        .select('id', { count: 'exact', head: true })
        .eq('status', 'active'),
    ]);

    setCommunityQueue({
      reports: reports.count || 0,
      livePosts: livePosts.count || 0,
    });
    setCommunityQueueLoading(false);
  }, [isAdminOrMod]);

  const fetchModerationItems = useCallback(async () => {
    if (!isAdminOrMod) return;

    setModerationLoading(true);

    const { data: reportsData, error } = await supabase
      .from('community_reports')
      .select('*')
      .in('status', ['open', 'in_review'])
      .order('created_at', { ascending: false })
      .limit(20);

    if (error) {
      console.error(error);
      setModerationItems([]);
      setModerationLoading(false);
      return;
    }

    const reports = (reportsData || []) as CommunityReport[];
    const postIds = reports.map((report) => report.post_id).filter(Boolean) as string[];
    const replyIds = reports.map((report) => report.reply_id).filter(Boolean) as string[];

    const [postsResult, repliesResult] = await Promise.all([
      postIds.length
        ? supabase.from('community_posts').select('*').in('id', postIds)
        : Promise.resolve({ data: [], error: null }),
      replyIds.length
        ? supabase.from('community_replies').select('*').in('id', replyIds)
        : Promise.resolve({ data: [], error: null }),
    ]);

    if (postsResult.error || repliesResult.error) {
      console.error(postsResult.error || repliesResult.error);
      setModerationItems([]);
      setModerationLoading(false);
      return;
    }

    const postsById = new Map(((postsResult.data || []) as CommunityPost[]).map((post) => [post.id, post]));
    const repliesById = new Map(((repliesResult.data || []) as CommunityReply[]).map((reply) => [reply.id, reply]));

    setModerationItems(
      reports
        .map((report) => ({
          report,
          post: report.post_id ? postsById.get(report.post_id) || null : null,
          reply: report.reply_id ? repliesById.get(report.reply_id) || null : null,
        }))
        .sort((left, right) => {
          const statusDelta = reportStatusOrder(left.report.status) - reportStatusOrder(right.report.status);
          if (statusDelta !== 0) return statusDelta;
          return new Date(right.report.created_at).getTime() - new Date(left.report.created_at).getTime();
        })
    );
    setModerationLoading(false);
  }, [isAdminOrMod]);

  const fetchAppealItems = useCallback(async () => {
    if (!isAdminOrMod) return;

    setAppealsLoading(true);

    const { data: appealsData, error } = await supabase
      .from('community_moderation_appeals')
      .select('*')
      .eq('status', 'open')
      .order('created_at', { ascending: false })
      .limit(20);

    if (error) {
      console.error(error);
      setAppealItems([]);
      setAppealsLoading(false);
      return;
    }

    const appeals = (appealsData || []) as CommunityModerationAppeal[];
    const logIds = appeals.map((appeal) => appeal.moderation_log_id);
    const userIds = Array.from(new Set(appeals.map((appeal) => appeal.appellant_user_id)));

    const [logsResult, profilesResult] = await Promise.all([
      logIds.length
        ? supabase.from('community_moderation_log').select('*').in('id', logIds)
        : Promise.resolve({ data: [], error: null }),
      userIds.length
        ? supabase.from('profiles').select('*').in('user_id', userIds)
        : Promise.resolve({ data: [], error: null }),
    ]);

    if (logsResult.error || profilesResult.error) {
      console.error(logsResult.error || profilesResult.error);
      setAppealItems([]);
      setAppealsLoading(false);
      return;
    }

    const logsById = new Map(((logsResult.data || []) as CommunityModerationLog[]).map((entry) => [entry.id, entry]));
    const profilesByUserId = new Map(((profilesResult.data || []) as ProfileRow[]).map((profile) => [profile.user_id, profile]));

    setAppealItems(
      appeals.map((appeal) => ({
        appeal,
        moderationLog: logsById.get(appeal.moderation_log_id) || null,
        profile: profilesByUserId.get(appeal.appellant_user_id) || null,
      }))
    );
    setAppealsLoading(false);
  }, [isAdminOrMod]);

  useEffect(() => {
    void fetchCommunityQueue();
    void fetchModerationItems();
    void fetchAppealItems();
  }, [fetchAppealItems, fetchCommunityQueue, fetchModerationItems]);

  const fetchRuntimeItems = useCallback(async () => {
    if (!isAdmin) return;

    setRuntimeLoading(true);

    const { data: runtimeData, error } = await supabase
      .from('app_runtime_events' as any)
      .select('*')
      .eq('status', 'open')
      .order('created_at', { ascending: false })
      .limit(12);

    if (error) {
      console.error(error);
      setRuntimeItems([]);
      setRuntimeLoading(false);
      return;
    }

    const events = (runtimeData || []) as RuntimeEventItem['event'][];
    const userIds = Array.from(new Set(events.map((entry) => entry.user_id).filter(Boolean)));
    const profilesResult = userIds.length
      ? await supabase.from('profiles').select('*').in('user_id', userIds)
      : { data: [], error: null };

    if (profilesResult.error) {
      console.error(profilesResult.error);
      setRuntimeItems(events.map((event) => ({ event, profile: null })));
      setRuntimeLoading(false);
      return;
    }

    const profilesByUserId = new Map(((profilesResult.data || []) as ProfileRow[]).map((profile) => [profile.user_id, profile]));
    setRuntimeItems(events.map((event) => ({ event, profile: profilesByUserId.get(event.user_id) || null })));
    setRuntimeLoading(false);
  }, [isAdmin]);

  const fetchRestrictedMembers = useCallback(async () => {
    if (!isAdmin) return;

    setRestrictedMembersLoading(true);

    const [profilesResult, logsResult, reportsResult, postsResult, repliesResult] = await Promise.all([
      supabase
        .from('profiles')
        .select('*')
        .neq('community_access_status', 'active')
        .order('updated_at', { ascending: false })
        .limit(10),
      supabase
        .from('community_moderation_log')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(100),
      supabase
        .from('community_reports')
        .select('*')
        .in('status', ['open', 'in_review'])
        .order('created_at', { ascending: false })
        .limit(100),
      supabase
        .from('community_posts')
        .select('id, user_id'),
      supabase
        .from('community_replies')
        .select('id, user_id'),
    ]);

    if (profilesResult.error || logsResult.error || reportsResult.error || postsResult.error || repliesResult.error) {
      console.error(profilesResult.error || logsResult.error || reportsResult.error || postsResult.error || repliesResult.error);
      setRestrictedMembers([]);
      setRestrictedMembersLoading(false);
      return;
    }

    const profiles = (profilesResult.data || []) as ProfileRow[];
    const logs = (logsResult.data || []) as CommunityModerationLog[];
    const reports = (reportsResult.data || []) as CommunityReport[];
    const posts = (postsResult.data || []) as Pick<CommunityPost, 'id' | 'user_id'>[];
    const replies = (repliesResult.data || []) as Pick<CommunityReply, 'id' | 'user_id'>[];

    const postUserById = new Map(posts.map((post) => [post.id, post.user_id]));
    const replyUserById = new Map(replies.map((reply) => [reply.id, reply.user_id]));

    setRestrictedMembers(
      profiles.map((profile) => {
        const latestAction = logs.find((entry) => entry.target_user_id === profile.user_id) || null;
        const unresolvedReports = reports.filter((report) => {
          if (report.post_id && postUserById.get(report.post_id) === profile.user_id) return true;
          if (report.reply_id && replyUserById.get(report.reply_id) === profile.user_id) return true;
          return false;
        }).length;

        return {
          userId: profile.user_id,
          profile,
          unresolvedReports,
          latestAction,
        };
      })
    );
    setRestrictedMembersLoading(false);
  }, [isAdmin]);

  useEffect(() => {
    void fetchRestrictedMembers();
  }, [fetchRestrictedMembers]);

  useEffect(() => {
    void fetchRuntimeItems();
  }, [fetchRuntimeItems]);

  const logModerationAction = useCallback(async ({
    action,
    targetUserId,
    reportId,
    postId,
    replyId,
    note,
  }: {
    action: string;
    targetUserId: string;
    reportId?: string | null;
    postId?: string | null;
    replyId?: string | null;
    note?: string | null;
  }) => {
    if (!user) return;

    const { error } = await supabase.from('community_moderation_log').insert({
      target_user_id: targetUserId,
      moderator_user_id: user.id,
      report_id: reportId || null,
      post_id: postId || null,
      reply_id: replyId || null,
      action,
      note: note || null,
    } as TablesInsert<'community_moderation_log'>);

    if (error) console.error(error);
  }, [user]);

  const setReportStatus = async (
    item: ModerationItem,
    status: 'in_review' | 'dismissed',
    resolution?: string | null,
  ) => {
    if (!user) return;
    setModeratingId(item.report.id);

    const target = item.reply || item.post;
    const moderatorNote = reportNoteDrafts[item.report.id]?.trim() || item.report.moderator_note || null;

    const { error } = await supabase
      .from('community_reports')
      .update({
        status,
        resolution: resolution || null,
        reviewed_at: status === 'dismissed' ? new Date().toISOString() : null,
        reviewed_by: status === 'dismissed' ? user.id : null,
        moderator_note: moderatorNote,
      })
      .eq('id', item.report.id);

    if (error) {
      console.error(error);
      toast.error('Could not update that report.');
    } else {
      if (target) {
        await logModerationAction({
          action: status === 'dismissed' ? 'report_dismissed' : 'report_in_review',
          targetUserId: target.user_id,
          reportId: item.report.id,
          postId: item.post?.id || null,
          replyId: item.reply?.id || null,
          note: moderatorNote || resolution || item.report.reason || null,
        });
      }
      toast.success(status === 'dismissed' ? 'Report dismissed.' : 'Report moved into review.');
      await Promise.all([fetchCommunityQueue(), fetchModerationItems(), fetchRestrictedMembers(), fetchAppealItems()]);
    }

    setModeratingId(null);
  };

  const updateModerationTarget = async (
    item: ModerationItem,
    status: 'active' | 'hidden',
    resolveReport = false
  ) => {
    const target = item.reply || item.post;
    if (!target) return;

    setModeratingId(item.report.id);

    const targetTable = item.reply ? 'community_replies' : 'community_posts';
    const targetId = target.id;
    const moderatorNote = reportNoteDrafts[item.report.id]?.trim() || item.report.moderator_note || null;

    const operations: Promise<any>[] = [
      supabase
        .from(targetTable)
        .update({ status })
        .eq('id', targetId),
    ];

    if (resolveReport && user) {
      operations.push(
        supabase
          .from('community_reports')
          .update({
            status: 'actioned',
            resolution: status === 'hidden' ? 'content_hidden' : 'content_restored',
            reviewed_at: new Date().toISOString(),
            reviewed_by: user.id,
            moderator_note: moderatorNote,
          })
          .eq('id', item.report.id)
      );
    }

    const results = await Promise.all(operations);
    const hasError = results.some((result) => result.error);

    if (hasError) {
      console.error(results);
      toast.error(`Could not ${status === 'hidden' ? 'hide' : 'restore'} that target.`);
    } else {
      await logModerationAction({
        action: status === 'hidden' ? 'content_hidden' : 'content_restored',
        targetUserId: target.user_id,
        reportId: item.report.id,
        postId: item.post?.id || null,
        replyId: item.reply?.id || null,
        note: moderatorNote || item.report.reason || null,
      });
      toast.success(
        status === 'hidden'
          ? resolveReport
            ? 'Target hidden and report resolved.'
            : 'Target hidden.'
          : 'Target restored.'
      );
      await Promise.all([fetchCommunityQueue(), fetchModerationItems(), fetchRestrictedMembers(), fetchAppealItems()]);
    }

    setModeratingId(null);
  };

  const reviewAppeal = async (item: AppealQueueItem, status: 'accepted' | 'declined') => {
    if (!user) return;

    setReviewingAppealId(item.appeal.id);
    const resolutionNote = appealResolutionDrafts[item.appeal.id]?.trim() || null;

    const operations: Promise<any>[] = [
      supabase
        .from('community_moderation_appeals')
        .update({
          status,
          resolution_note: resolutionNote,
          reviewed_at: new Date().toISOString(),
          reviewed_by: user.id,
        })
        .eq('id', item.appeal.id),
    ];

    const shouldRestoreAccess =
      status === 'accepted'
      && item.moderationLog?.action === 'member_suspended'
      && item.profile?.community_access_status !== 'active';

    if (shouldRestoreAccess) {
      operations.push(
        supabase
          .from('profiles')
          .update({ community_access_status: 'active' })
          .eq('user_id', item.appeal.appellant_user_id)
      );
    }

    operations.push(
      supabase.from('community_moderation_log').insert({
        target_user_id: item.appeal.appellant_user_id,
        moderator_user_id: user.id,
        report_id: item.moderationLog?.report_id || null,
        post_id: item.moderationLog?.post_id || null,
        reply_id: item.moderationLog?.reply_id || null,
        action: status === 'accepted' ? 'appeal_accepted' : 'appeal_declined',
        note: resolutionNote,
        member_visible: true,
        member_message:
          resolutionNote
          || (status === 'accepted'
            ? 'Your appeal has been accepted. Community access has been reviewed and updated.'
            : 'Your appeal has been reviewed and the original moderation action is staying in place.'),
      } as TablesInsert<'community_moderation_log'>)
    );

    const results = await Promise.all(operations);

    if (results.some((result) => result.error)) {
      console.error(results);
      toast.error('Could not update that appeal.');
    } else {
      toast.success(status === 'accepted' ? 'Appeal accepted.' : 'Appeal declined.');
      setAppealResolutionDrafts((current) => ({ ...current, [item.appeal.id]: '' }));
      await Promise.all([fetchAppealItems(), fetchRestrictedMembers()]);
    }

    setReviewingAppealId(null);
  };

  const copyGuidelines = () => {
    navigator.clipboard.writeText(COMMUNITY_GUIDELINES);
    setGuidelinesCopied(true);
    toast.success('Guidelines copied to clipboard');
    setTimeout(() => setGuidelinesCopied(false), 2000);
  };

  const QUARTERLY_PROMPT = `I run a UK consumer app called Adulting (adultingapp.uk) — a life admin assistant for money, home, health, family and work admin. Built in Lovable with a Supabase backend.

Time for my quarterly review. Please help me with:

1. What content likely needs updating based on recent UK law, benefit rates, or NHS changes?
2. Any new UK policies or benefits I should add?
3. What features should I prioritise building next?
4. Any UX or product improvements worth considering?

Current sections: Money (Budget/Benefits/Survival), Home (one Home flow for profile, bills, routines, and admin), Health (My Health/Wellbeing), Work (Rights/Self-Employed/Tools), My People (Calendar/Admin/Chores), visible helper packs at section bottoms, accordioned support packs, plus dedicated Guides and Community destinations in More.`;

  const copyQuarterlyPrompt = () => {
    navigator.clipboard.writeText(QUARTERLY_PROMPT);
    setQuarterlyPromptCopied(true);
    toast.success('Quarterly review prompt copied');
    setTimeout(() => setQuarterlyPromptCopied(false), 2000);
  };

  const resolveRuntimeEvent = async (eventId: string) => {
    if (!user) return;

    setResolvingRuntimeId(eventId);
    const resolutionNote = runtimeResolutionDrafts[eventId]?.trim() || null;

    const { error } = await supabase
      .from('app_runtime_events' as any)
      .update({
        status: 'resolved',
        resolved_at: new Date().toISOString(),
        resolved_by: user.id,
        resolution_note: resolutionNote,
      })
      .eq('id', eventId);

    if (error) {
      console.error(error);
      toast.error('Could not resolve that runtime event.');
    } else {
      toast.success('Runtime event resolved.');
      setRuntimeResolutionDrafts((current) => ({ ...current, [eventId]: '' }));
      await fetchRuntimeItems();
    }

    setResolvingRuntimeId(null);
  };

  // Compute scheduled reminders
  const scheduledReminders = useMemo(() => {
    const today = new Date();
    const reminders: { emoji: string; label: string; dueDate: Date; daysAway: number; hasPrompt?: boolean }[] = [];

    // Employment rights - earliest due card
    const empSection = sections.find(s => s.section === 'employment-rights');
    if (empSection) {
      let earliestDue: Date | null = null;
      for (const card of [...empSection.overdue, ...empSection.dueSoon, ...empSection.upToDate]) {
        const { dueDate } = getCardDueInfo(card);
        if (!earliestDue || dueDate < earliestDue) earliestDue = dueDate;
      }
      if (earliestDue) {
        reminders.push({ emoji: '📋', label: 'Review Employment Rights content', dueDate: earliestDue, daysAway: differenceInDays(earliestDue, today) });
      }
    }

    // Benefits figures - 1 April each year
    const apr1 = new Date(today.getFullYear(), 3, 1);
    const benefitsDue = apr1 > today ? apr1 : new Date(today.getFullYear() + 1, 3, 1);
    reminders.push({ emoji: '💰', label: 'Review Benefits figures', dueDate: benefitsDue, daysAway: differenceInDays(benefitsDue, today) });

    // Quarterly app review - every 3 months from 11 March 2026
    const baseDate = new Date(2026, 2, 11); // 11 March 2026
    let nextQuarterly = new Date(baseDate);
    while (nextQuarterly <= today) {
      nextQuarterly = addMonths(nextQuarterly, 3);
    }
    reminders.push({ emoji: '🔍', label: 'Quarterly app review with Claude/ChatGPT', dueDate: nextQuarterly, daysAway: differenceInDays(nextQuarterly, today), hasPrompt: true });

    return reminders;
  }, [sections]);

  const startHereTiles: Array<{ label: string; value: string | number; detail: string; onPress?: () => void }> = [
    ...(isAdmin
      ? [
          {
            label: 'Total users',
            value: statsLoading ? '—' : stats.users,
            detail: 'Open user admin.',
            onPress: () => navigate('/admin/community-members'),
          },
        ]
      : []),
    {
      label: 'Reports',
      value: communityQueueLoading ? '—' : communityQueue.reports,
      detail: 'Open or in-review moderation reports.',
    },
    {
      label: 'Live posts',
      value: communityQueueLoading ? '—' : communityQueue.livePosts,
      detail: 'Active community posts visible right now.',
    },
    ...(isAdmin
      ? [
          {
            label: 'Appeals',
            value: appealsLoading ? '—' : appealItems.length,
            detail: 'Pending moderation appeals waiting for review.',
          },
          {
            label: 'Restricted',
            value: restrictedMembersLoading ? '—' : restrictedMembers.length,
            detail: 'Suspended or banned community accounts.',
          },
          {
            label: 'Runtime',
            value: runtimeLoading ? '—' : runtimeItems.length,
            detail: 'Open beta/runtime issues needing attention.',
          },
          {
            label: 'Due content',
            value: contentLoading ? '—' : totalDue,
            detail: 'Content cards due for review soon or now.',
          },
        ]
      : []),
  ];

  const toolLaunchers = [
    ...(isAdmin
      ? [
          {
            eyebrow: 'People',
            title: 'User admin',
            summary: 'Open member records, roles, and community access controls.',
            detail: statsLoading ? 'Loading member totals…' : `${stats.users} registered users`,
            icon: Users,
            onPress: () => navigate('/admin/community-members'),
          },
          {
            eyebrow: 'Content',
            title: 'Content health',
            summary: 'Review guidance cards, due content, and shared product copy.',
            detail:
              contentLoading || statsLoading
                ? 'Loading content totals…'
                : `${stats.content} content cards · ${totalDue} due for review`,
            icon: FileText,
            onPress: () => navigate('/admin/content'),
          },
        ]
      : []),
  ];

  const workspaceTitle = isAdmin ? 'Admin workspace' : 'Moderator workspace';
  const workspaceSummary = isAdmin
    ? 'Use this as the working launcher for moderation, people, content, and operational review.'
    : 'Use this as the working launcher for moderation queues, guidance, and escalation support.';
  const accessSummary = isAdmin
    ? 'This signed-in account can open content health, member intervention, analytics, runtime watch, and moderation tools.'
    : 'This signed-in account can review reports, apply moderation actions, and use the guidance pack without leaving Adulting.';

  if (loading) {
    return (
      <AppPage className="animate-fade-in">
        <AppLoadingState
          title="Loading..."
          body="Checking whether this account can open the admin and moderation tools."
        />
      </AppPage>
    );
  }
  if (!isAdminOrMod) return null;

  return (
    <div className="min-h-screen bg-background">
      <div className="sticky top-0 z-40 border-b border-border bg-card/95 backdrop-blur">
        <AppHeaderBar
          title="Admin & Moderation"
          topPadding="$md"
          bottomPadding="$sm"
          action={
            <AppButton tone="ghost" size="sm" onPress={() => navigate(-1)}>
              <ArrowLeft size={16} />
              Back
            </AppButton>
          }
        />
      </div>

      <AppPage className="max-w-5xl animate-fade-in">
        <div className="space-y-5">
          <AdminWorkspaceSection
            eyebrow="Start here"
            title={workspaceTitle}
            subtitle={workspaceSummary}
            accented
            action={
              <AppButton size="sm" tone="outline" onPress={() => navigate('/admin/moderation-guidelines')}>
                Open moderation guidance
              </AppButton>
            }
          >
            <div className="rounded-2xl border border-primary/15 bg-background/95 px-4 py-4">
              <div className="flex items-start gap-3">
                <div className="mt-0.5 flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-primary/10 text-primary">
                  {isAdmin ? <Crown size={18} /> : <Shield size={18} />}
                </div>
                <div className="min-w-0 flex-1 space-y-1">
                  <p className="text-sm font-semibold text-foreground">{isAdmin ? 'Full admin access' : 'Moderator access'}</p>
                  <p className="text-xs leading-relaxed text-muted-foreground">{accessSummary}</p>
                  {user?.email ? <p className="text-[11px] font-medium text-primary">{user.email}</p> : null}
                </div>
              </div>
            </div>

            <div className="grid gap-3 md:grid-cols-3">
              {startHereTiles.map((item) => (
                <AdminSummaryTile
                  key={item.label}
                  label={item.label}
                  value={item.value}
                  detail={item.detail}
                  onPress={item.onPress}
                />
              ))}
            </div>
          </AdminWorkspaceSection>

          <AdminWorkspaceSection
            eyebrow="Safety"
            title="Live moderation work"
            subtitle="Keep open moderation, member access, and appeals together instead of scattering them across the page."
            accented
            collapsible
            open={openAdminSections.moderation}
            onToggle={() => toggleAdminSection('moderation')}
          >
            <div className="space-y-3">
              {moderationLoading ? (
                <div className="rounded-xl border border-border bg-background px-4 py-4">
                  <p className="text-sm text-muted-foreground">Loading reports…</p>
                </div>
              ) : moderationItems.length === 0 ? (
                <div className="rounded-xl border border-border bg-background px-4 py-4">
                  <p className="text-sm font-medium text-foreground">No open moderation items.</p>
                  <p className="mt-1 text-xs text-muted-foreground">Open and in-review reports will show here during beta.</p>
                </div>
              ) : (
                moderationItems.map((item) => {
                  const target = item.reply || item.post;
                  const targetType = item.reply ? 'Reply' : 'Post';
                  const targetStatus = target?.status;
                  const targetPreview = target?.body || 'This content is no longer available.';
                  const statusMeta = reportStatusMeta(item.report.status);

                  return (
                    <div key={item.report.id} className="rounded-xl border border-border bg-background px-4 py-4 space-y-3">
                      <div className="flex items-start gap-3">
                        <div className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-primary/10 text-primary">
                          <Shield size={16} />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-center gap-2">
                            <p className="text-sm font-semibold text-foreground">{item.report.reason}</p>
                            <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-medium text-primary">
                              {targetType}
                            </span>
                            <span className={cn('rounded-full px-2 py-0.5 text-[10px] font-medium', statusMeta.className)}>
                              {statusMeta.label}
                            </span>
                            {targetStatus ? (
                              <span
                                className={cn(
                                  'rounded-full px-2 py-0.5 text-[10px] font-medium',
                                  targetStatus === 'hidden' ? 'bg-amber-100 text-amber-800' : 'bg-green-100 text-green-800'
                                )}
                              >
                                {targetStatus === 'hidden' ? 'Hidden' : 'Live'}
                              </span>
                            ) : null}
                            <span className="text-[10px] font-medium text-muted-foreground">
                              {formatDistanceToNow(new Date(item.report.created_at), { addSuffix: true })}
                            </span>
                          </div>
                          <p className="mt-1 text-[11px] text-muted-foreground">
                            Target author: {target?.author_name || 'Unknown'}
                          </p>
                        </div>
                      </div>

                      <div className="rounded-xl border border-border bg-card px-4 py-3">
                        <p className="mb-2 text-[11px] font-medium uppercase tracking-[0.08em] text-muted-foreground">Reported content</p>
                        <p className="whitespace-pre-wrap text-sm leading-relaxed text-foreground">{targetPreview}</p>
                      </div>

                      {item.report.details ? (
                        <div className="rounded-xl border border-border bg-card px-4 py-3">
                          <p className="mb-2 text-[11px] font-medium uppercase tracking-[0.08em] text-muted-foreground">Reporter notes</p>
                          <p className="whitespace-pre-wrap text-sm leading-relaxed text-foreground">{item.report.details}</p>
                        </div>
                      ) : null}

                      <div className="rounded-xl border border-border bg-card px-4 py-3 space-y-2">
                        <p className="text-[11px] font-medium uppercase tracking-[0.08em] text-muted-foreground">Moderator note</p>
                        <AppTextArea
                          value={reportNoteDrafts[item.report.id] ?? item.report.moderator_note ?? ''}
                          onChange={(event) => setReportNoteDrafts((current) => ({ ...current, [item.report.id]: event.target.value }))}
                          rows={2}
                          maxLength={400}
                          placeholder="Optional internal note for the moderation record."
                          className="bg-background"
                        />
                      </div>

                      <div className="flex flex-wrap gap-2">
                        {item.report.status === 'open' ? (
                          <AppButton onPress={() => setReportStatus(item, 'in_review')} disabled={moderatingId === item.report.id} tone="outline" size="sm">
                            <CheckCheck size={14} />
                            Take into review
                          </AppButton>
                        ) : null}
                        {target && targetStatus === 'active' ? (
                          <AppButton onPress={() => updateModerationTarget(item, 'hidden', true)} disabled={moderatingId === item.report.id} tone="outline" size="sm">
                            <EyeOff size={14} />
                            Hide and resolve
                          </AppButton>
                        ) : null}
                        {target && targetStatus === 'hidden' ? (
                          <AppButton onPress={() => updateModerationTarget(item, 'active', false)} disabled={moderatingId === item.report.id} tone="outline" size="sm">
                            <RotateCcw size={14} />
                            Restore target
                          </AppButton>
                        ) : null}
                        <AppButton onPress={() => setReportStatus(item, 'dismissed', 'no_action_needed')} disabled={moderatingId === item.report.id} tone="outline" size="sm">
                          <CheckCheck size={14} />
                          Dismiss report
                        </AppButton>
                        {isAdmin && target ? (
                          <AppButton onPress={() => navigate(`/admin/community-members/${target.user_id}`)} tone="outline" size="sm">
                            <UserRound size={14} />
                            Manage member
                          </AppButton>
                        ) : null}
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            {isAdmin ? (
              <div className="grid gap-3 lg:grid-cols-2">
                <div className="space-y-3">
                  <div>
                    <AppSectionHeader
                      title="Community access"
                      subtitle="Suspended and banned member records stay together here."
                    />
                  </div>
                  {restrictedMembersLoading ? (
                    <div className="rounded-xl border border-border bg-background px-4 py-4">
                      <p className="text-sm text-muted-foreground">Loading member actions…</p>
                    </div>
                  ) : restrictedMembers.length === 0 ? (
                    <div className="rounded-xl border border-border bg-background px-4 py-4">
                      <p className="text-sm font-medium text-foreground">No members currently suspended.</p>
                      <p className="mt-1 text-xs text-muted-foreground">Warnings and suspensions will show here once they exist.</p>
                    </div>
                  ) : (
                    restrictedMembers.map((member) => (
                      <AppPanelButton
                        key={member.userId}
                        onClick={() => navigate(`/admin/community-members/${member.userId}`)}
                        className="rounded-xl border border-border bg-background p-4"
                      >
                        <div className="flex items-center gap-3">
                          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-primary/10 text-primary">
                            <UserRound size={16} />
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-medium text-foreground">{formatAdminMemberName(member.profile?.first_name, memberEmailByUserId[member.userId])}</p>
                            <p className="text-[11px] text-muted-foreground">
                              {member.unresolvedReports} open report{member.unresolvedReports === 1 ? '' : 's'}
                              {member.latestAction ? ` · last action ${formatDistanceToNow(new Date(member.latestAction.created_at), { addSuffix: true })}` : ''}
                            </p>
                          </div>
                          <span
                            className={cn(
                              'rounded-full px-2 py-0.5 text-[10px] font-medium',
                              member.profile?.community_access_status === 'suspended'
                                ? 'bg-amber-100 text-amber-800'
                                : 'bg-destructive/10 text-destructive'
                            )}
                          >
                            {member.profile?.community_access_status === 'suspended' ? 'Suspended' : 'Banned'}
                          </span>
                        </div>
                      </AppPanelButton>
                    ))
                  )}
                </div>

                <div className="space-y-3">
                  <div>
                    <AppSectionHeader
                      title="Open appeals"
                      subtitle="Appeals stay in the same working area as moderation decisions."
                    />
                  </div>
                  {appealsLoading ? (
                    <div className="rounded-xl border border-border bg-background px-4 py-4">
                      <p className="text-sm text-muted-foreground">Loading appeals…</p>
                    </div>
                  ) : appealItems.length === 0 ? (
                    <div className="rounded-xl border border-border bg-background px-4 py-4">
                      <p className="text-sm font-medium text-foreground">No open appeals.</p>
                      <p className="mt-1 text-xs text-muted-foreground">Member appeals will show here as soon as they are submitted.</p>
                    </div>
                  ) : (
                    appealItems.map((item) => (
                      <div key={item.appeal.id} className="rounded-xl border border-border bg-background px-4 py-4 space-y-3">
                        <div className="flex items-start gap-3">
                          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-primary/10 text-primary">
                            <UserRound size={16} />
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="flex flex-wrap items-center gap-2">
                              <p className="text-sm font-semibold text-foreground">{formatAdminMemberName(item.profile?.first_name, memberEmailByUserId[item.appeal.appellant_user_id])}</p>
                              <span className="text-[10px] text-muted-foreground">
                                {formatDistanceToNow(new Date(item.appeal.created_at), { addSuffix: true })}
                              </span>
                            </div>
                            <p className="mt-1 text-xs text-muted-foreground">
                              Original action: {item.moderationLog ? item.moderationLog.action.replace(/_/g, ' ') : 'Unknown'}
                            </p>
                          </div>
                          <AppButton onPress={() => navigate(`/admin/community-members/${item.appeal.appellant_user_id}`)} tone="outline" size="sm">
                            <UserRound size={14} />
                            Manage member
                          </AppButton>
                        </div>

                        <div className="rounded-xl border border-border bg-card px-4 py-3">
                          <p className="mb-2 text-[11px] font-medium uppercase tracking-[0.08em] text-muted-foreground">Member appeal</p>
                          <p className="whitespace-pre-wrap text-sm leading-relaxed text-foreground">{item.appeal.message}</p>
                        </div>

                        <AppTextArea
                          value={appealResolutionDrafts[item.appeal.id] ?? ''}
                          onChange={(event) => setAppealResolutionDrafts((current) => ({ ...current, [item.appeal.id]: event.target.value }))}
                          rows={2}
                          maxLength={400}
                          placeholder="Optional resolution note for the member and the moderation record."
                          className="bg-card"
                        />

                        <div className="flex flex-wrap gap-2">
                          <AppButton onPress={() => reviewAppeal(item, 'accepted')} disabled={reviewingAppealId === item.appeal.id} tone="outline" size="sm">
                            <RotateCcw size={14} />
                            Accept appeal
                          </AppButton>
                          <AppButton onPress={() => reviewAppeal(item, 'declined')} disabled={reviewingAppealId === item.appeal.id} tone="outline" size="sm">
                            <CheckCheck size={14} />
                            Decline appeal
                          </AppButton>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            ) : null}
          </AdminWorkspaceSection>

          {isAdmin ? (
            <AdminWorkspaceSection
              eyebrow="Tools"
              title="People and content tools"
              subtitle="Keep deeper admin tools separate from the live moderation queue."
              collapsible
              open={openAdminSections.tools}
              onToggle={() => toggleAdminSection('tools')}
            >
              <div className="grid gap-3 md:grid-cols-2">
                {toolLaunchers.map((launcher) => (
                  <AdminLauncherCard
                    key={launcher.title}
                    eyebrow={launcher.eyebrow}
                    title={launcher.title}
                    summary={launcher.summary}
                    detail={launcher.detail}
                    icon={launcher.icon}
                    onPress={launcher.onPress}
                  />
                ))}
              </div>
              <div className="grid gap-3 md:grid-cols-3">
                <AdminSummaryTile
                  label="Registered users"
                  value={statsLoading ? '—' : stats.users}
                  detail="Total member records in Adulting."
                />
                <AdminSummaryTile
                  label="Benefits scans"
                  value={statsLoading ? '—' : stats.benefits}
                  detail="Tracked benefits-scan records across the product."
                />
                <AdminSummaryTile
                  label="Content cards"
                  value={statsLoading ? '—' : stats.content}
                  detail="Shared support cards currently in the library."
                />
              </div>
            </AdminWorkspaceSection>
          ) : null}

          {isAdmin ? (
            <AdminWorkspaceSection
              eyebrow="Ops"
              title="Operational review"
              subtitle="Analytics, runtime watch, and recurring reminders live here so they do not compete with urgent moderation."
              collapsible
              open={openAdminSections.ops}
              onToggle={() => toggleAdminSection('ops')}
            >
              {analyticsLoading ? (
                <div className="rounded-xl border border-border bg-background px-4 py-4">
                  <p className="text-sm text-muted-foreground">Loading usage summary…</p>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="grid gap-3 md:grid-cols-3">
                    <AdminSummaryTile label="Active users" value={analyticsSummary.activeUsers7d} detail="Signed-in members active in the last 7 days." />
                    <AdminSummaryTile label="Page views" value={analyticsSummary.pageViews7d} detail="Tracked authenticated page views only." />
                    <AdminSummaryTile label="Due content" value={contentLoading ? '—' : totalDue} detail="Cards or guides due for review now or soon." />
                  </div>

                  <div className="grid gap-3 lg:grid-cols-2">
                    <div className="rounded-xl border border-border bg-background px-4 py-4 space-y-3">
                      <div>
                        <p className="text-sm font-semibold text-foreground">Top routes</p>
                        <p className="mt-1 text-[11px] text-muted-foreground">A lightweight owned analytics baseline for where signed-in members actually spend time.</p>
                      </div>
                      {analyticsSummary.topRoutes.length === 0 ? (
                        <p className="text-sm text-muted-foreground">No route usage logged yet.</p>
                      ) : (
                        analyticsSummary.topRoutes.map((item) => (
                          <div key={item.path} className="flex items-center justify-between gap-3 rounded-xl border border-border bg-card px-4 py-3">
                            <p className="text-sm font-medium text-foreground">{item.path}</p>
                            <span className="text-xs font-semibold text-muted-foreground">{item.count}</span>
                          </div>
                        ))
                      )}
                    </div>

                    <div className="rounded-xl border border-border bg-background px-4 py-4 space-y-3">
                      <div>
                        <p className="text-sm font-semibold text-foreground">Key actions</p>
                        <p className="mt-1 text-[11px] text-muted-foreground">Keep the most important non-page-view behaviour visible.</p>
                      </div>
                      {analyticsSummary.keyEvents.length === 0 ? (
                        <p className="text-sm text-muted-foreground">No non-page-view actions logged yet.</p>
                      ) : (
                        analyticsSummary.keyEvents.map((item) => (
                          <div key={item.label} className="flex items-center justify-between gap-3 rounded-xl border border-border bg-card px-4 py-3">
                            <p className="text-sm text-foreground">{item.label}</p>
                            <span className="text-xs font-semibold text-muted-foreground">{item.count}</span>
                          </div>
                        ))
                      )}
                    </div>
                  </div>

                  <div className="grid gap-3 lg:grid-cols-2">
                    <div className="space-y-3">
                      <div>
                        <AppSectionHeader
                          title="Runtime watch"
                          subtitle="Client-side failures from signed-in sessions surface here during beta."
                        />
                      </div>
                      {runtimeLoading ? (
                        <div className="rounded-xl border border-border bg-background px-4 py-4">
                          <p className="text-sm text-muted-foreground">Loading runtime events…</p>
                        </div>
                      ) : runtimeItems.length === 0 ? (
                        <div className="rounded-xl border border-border bg-background px-4 py-4">
                          <p className="text-sm font-medium text-foreground">No open runtime issues.</p>
                          <p className="mt-1 text-xs text-muted-foreground">Client-side failures from signed-in sessions will appear here during beta.</p>
                        </div>
                      ) : (
                        runtimeItems.map(({ event, profile }) => (
                          <div key={event.id} className="rounded-xl border border-border bg-background px-4 py-4 space-y-3">
                            <div className="flex items-start gap-3">
                              <div className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-primary/10 text-primary">
                                <Activity size={16} />
                              </div>
                              <div className="min-w-0 flex-1">
                                <div className="flex flex-wrap items-center gap-2">
                                  <p className="text-sm font-semibold text-foreground">{event.source}</p>
                                  <span className="text-[10px] font-medium text-muted-foreground">
                                    {formatDistanceToNow(new Date(event.created_at), { addSuffix: true })}
                                  </span>
                                </div>
                                <p className="mt-1 text-[11px] text-muted-foreground">
                                  {formatAdminMemberName(profile?.first_name, memberEmailByUserId[event.user_id])}{event.path ? ` · ${event.path}` : ''}
                                </p>
                              </div>
                            </div>

                            <div className="rounded-xl border border-border bg-card px-4 py-3">
                              <p className="mb-2 text-[11px] font-medium uppercase tracking-[0.08em] text-muted-foreground">Runtime message</p>
                              <p className="whitespace-pre-wrap text-sm leading-relaxed text-foreground">{event.message}</p>
                            </div>

                            <AppTextArea
                              value={runtimeResolutionDrafts[event.id] ?? ''}
                              onChange={(record) => setRuntimeResolutionDrafts((current) => ({ ...current, [event.id]: record.target.value }))}
                              rows={2}
                              maxLength={400}
                              placeholder="Optional resolution note for the runtime record."
                              className="bg-card"
                            />

                            <div className="flex flex-wrap gap-2">
                              <AppButton onPress={() => resolveRuntimeEvent(event.id)} disabled={resolvingRuntimeId === event.id} tone="outline" size="sm">
                                <CheckCircle2 size={14} />
                                Resolve issue
                              </AppButton>
                            </div>
                          </div>
                        ))
                      )}
                    </div>

                    <div className="space-y-3">
                      <div>
                        <AppSectionHeader
                          title="Scheduled reminders"
                          subtitle="Recurring review dates for content, figures, growth, and quarterly product review."
                        />
                      </div>
                      <div className="overflow-hidden rounded-xl border border-border bg-background divide-y divide-border">
                        {scheduledReminders.map((item) => (
                          <div key={item.label} className="flex items-center gap-3 px-4 py-3">
                            <span className="shrink-0 text-sm">{item.emoji}</span>
                            <div className="min-w-0 flex-1">
                              <p className="text-xs font-medium text-foreground">{item.label}</p>
                              <p className={cn('text-[10px]', item.daysAway < 0 ? 'text-destructive' : 'text-muted-foreground')}>
                                {format(item.dueDate, 'd MMM yyyy')} · {item.daysAway < 0 ? `${Math.abs(item.daysAway)} days overdue` : item.daysAway === 0 ? 'Today' : `${item.daysAway} days away`}
                              </p>
                            </div>
                            {item.hasPrompt ? (
                              <AppIconButton onClick={copyQuarterlyPrompt} className="shrink-0" title="Copy AI session prompt">
                                {quarterlyPromptCopied ? <ClipboardCheck size={14} className="text-primary" /> : <Copy size={14} className="text-muted-foreground" />}
                              </AppIconButton>
                            ) : null}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </AdminWorkspaceSection>
          ) : null}

          <AdminWorkspaceSection
            eyebrow="Reference"
            title="Guidance and reusable notes"
            subtitle="Keep handbook material visible, but out of the main live-work path."
            collapsible
            open={openAdminSections.reference}
            onToggle={() => toggleAdminSection('reference')}
          >
            <div className="grid gap-3 lg:grid-cols-[1.1fr_0.9fr]">
              <div className="space-y-3">
                <div className="overflow-hidden rounded-xl border border-border bg-background">
                  <AppPanelButton
                    onClick={() => setHandbookOpen(!handbookOpen)}
                    className="gap-3 rounded-none border-0 p-4 active:bg-muted/50"
                  >
                    <span className="text-lg">📖</span>
                    <span className="flex-1 text-sm font-semibold text-foreground">Moderator handbook</span>
                    <AppDisclosureGlyph open={handbookOpen} />
                  </AppPanelButton>
                  {handbookOpen ? (
                    <div className="animate-fade-in divide-y divide-border border-t border-border">
                      {HANDBOOK_SCENARIOS.map((scenario) => (
                        <div key={scenario.title}>
                          <AppPanelButton
                            onClick={() => setScenarioOpen(scenarioOpen === scenario.title ? null : scenario.title)}
                            className="gap-2 rounded-none border-0 px-4 py-3 active:bg-muted/50"
                          >
                            <span className="flex-1 text-xs font-semibold text-foreground">{scenario.title}</span>
                            <AppDisclosureGlyph open={scenarioOpen === scenario.title} />
                          </AppPanelButton>
                          {scenarioOpen === scenario.title ? (
                            <p className="animate-fade-in px-4 pb-3 text-xs leading-relaxed text-muted-foreground">{scenario.body}</p>
                          ) : null}
                        </div>
                      ))}
                    </div>
                  ) : null}
                </div>

                <div className="space-y-2">
                  {ESCALATION_STEPS.map((step) => (
                    <div key={step.title} className="rounded-xl border border-border bg-background px-4 py-3">
                      <p className="text-sm font-medium text-foreground">{step.title}</p>
                      <p className="mt-1 text-xs leading-relaxed text-muted-foreground">{step.body}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="space-y-3">
                <div className="rounded-xl border border-border bg-background px-4 py-4 space-y-3">
                  <div>
                    <p className="text-sm font-semibold text-foreground">Clipboard helpers</p>
                    <p className="mt-1 text-[11px] text-muted-foreground">Keep the most repeated replies and review prompts easy to reach.</p>
                  </div>
                  <AppButton onPress={copyGuidelines} tone="outline" width="100%" justifyContent="center">
                    {guidelinesCopied ? <ClipboardCheck size={16} className="text-primary" /> : <Copy size={16} className="text-muted-foreground" />}
                    {guidelinesCopied ? 'Copied!' : 'Copy community guidelines'}
                  </AppButton>
                  {isAdmin ? (
                    <AppButton onPress={copyQuarterlyPrompt} tone="outline" width="100%" justifyContent="center">
                      {quarterlyPromptCopied ? <ClipboardCheck size={16} className="text-primary" /> : <Copy size={16} className="text-muted-foreground" />}
                      {quarterlyPromptCopied ? 'Copied!' : 'Copy quarterly review prompt'}
                    </AppButton>
                  ) : null}
                </div>
              </div>
            </div>
          </AdminWorkspaceSection>
        </div>
      </AppPage>
    </div>
  );
}
